import React, { useState, useEffect, useContext, useRef } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import useAgentBuilderStore from "Store/useAgentBuilderStore";
import useRedeploymentPlanStore, { isPaicReductionLocation, isRedeploymentFlowLocation } from "Store/useRedeploymentPlanStore";
import useWorkforcePlanStore, { isWorkforceFlowLocation } from "Store/useWorkforcePlanStore";
import usePaicRedesignPlanStore, { isPaicRedesignFlowLocation } from "Store/usePaicRedesignPlanStore";
import { Dropdown, Image } from "antd";
import {
  ClusterOutlined,
  BookOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
  IdcardOutlined,
  LogoutOutlined,
  BellFilled,
  SettingOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";

import { AuthContext } from "State/AuthContext";
import useJobsStore from "Store/useJobsStore";
import useEntitlementsStore from "Store/useEntitlementsStore";
import useAuthStore from "Store/useAuthStore";
import useNotificationStoreV2 from "Store/useNotificationStoreV2";
import useTourStore from "Store/useTourStore";
import NotificationPanelV2 from "Components/NotificationPanelV2/NotificationPanelV2";
import Permission from "Components/Permission";
import { useHasAccess } from "Routes/hooks/useHasAccess";
import FeedbackV2 from "Components/FeedbackV2/FeedbackV2";
import SbPopover from "uiCore/SbPopover/SbPopover";
import SbSuccessAlert from "Components/SbAlertComponent/SbSuccessAlert";
import { isAskAllieEnabled } from "Utils/featureFlag";
import sbBtLogo from "Assets/Images/skillsbridge-logomark.png";
import { submitUserFeedback } from "Api/apiService";
import useConsentGate from "Components/LayoutV2/useConsentGate";
import { isWipOnly } from "Routes/RouteConfigV2";
import { closeDashboardWidgetForNavigation } from "Pages/Dashboard/dashboardWidgetHistory";

import {
  LayoutWrapperStyled,
  V2GlobalStyle,
  HeaderStyled,
  HeaderBridgeStyled,
  BodyWrapperStyled,
  SiderStyled,
  ContentStyled,
  LogoWrapperStyled,
  NavActionsStyled,
  TourHelpWrapperStyled,
  BellWrapperStyled,
  AvatarStyled,
  ProfileMenuStyled,
  ProfileMenuItemStyled,
  ProfileMenuDividerStyled,
  ProfileNameStyled,
  ProfileFullNameStyled,
  ProfileRoleStyled,
  NavItemStyled,
  NavIconWrapperStyled,
  SidebarScrollableStyled,
  SidebarFooterStyled,
  LegalNavItemStyled,
  FeedbackButtonWrapperStyled,
  FeedbackButtonStyled,
  ThumbsContainerStyled,
  AskAllieButtonWrapperStyled,
  SbIconStyled,
  SwitchRoleLabelStyled,
  CustomerLogoTextStyled,
} from "Components/LayoutV2/LayoutV2.styles";
import AskAllie from "uiCore/AskAllie";
import DashBoardIcon from "Components/SBLayoutIcon/DashBoardIcon";
import JobslibraryIcon from "Components/SBLayoutIcon/JobslibraryIcon";
import PeoplePlusAiIcon from "Components/SBLayoutIcon/PeoplePlusAiIcon";
import SkillsBridgersIcon from "Components/SBLayoutIcon/SkillsBridgersIcon";
import TalentMobilityIcon from "Components/SBLayoutIcon/TalentMobilityIcon";
import LearningCatalystIcon from "Components/SBLayoutIcon/LearningCatalystIcon";
import HeaderBridgeAnimation from "Components/LayoutV2/HeaderBridgeAnimation";
import SbSwitch from "uiCore/SbSwitch/SbSwitch";
import SbBadge from "uiCore/SbBadge/SbBadge";
import { SbTooltip } from "uiCore";
import FeedbackIcon from "Components/SBLayoutIcon/FeedbackIcon";
import LegalIcon from "Components/SBLayoutIcon/LegalIcon";

// ─── Constants ────────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  {
    key: "/dashboard",
    label: "Dashboard",
    icon: <DashBoardIcon />,
    // "inprogress" (not "view") — see the matching comment in RouteConfigV2.js.
    resource: "dashboard",
    actions: ["inprogress"],
  },
  {
    key: "/jobs-library",
    label: "Jobs Library",
    icon: <JobslibraryIcon fill="#FFF"/>,
    resource: "jobs_library",
    actions: ["view"],
  },
  {
    key: "/people+ai",
    label: "People + AI",
    icon: <PeoplePlusAiIcon/>,
    resource: "people_plus_ai",
    actions: ["view"],
  },
  // Org Design has its own "org_redesign" entitlement resource — PAIC is
  // only a data source it consumes, not its access gate.
  {
    key: "/org-design",
    label: "Org Design",
    icon: <ClusterOutlined />,
    resource: "org_redesign",
    actions: ["view"],
  },
  {
    key: "/skills-bridger",
    label: "Skills Bridger",
    icon: <SkillsBridgersIcon/>,
    resource: "skillsbridger",
    actions: ["inprogress"],
  },
  {
    key: "/talent-mobility",
    label: "Talent Mobility",
    icon: <TalentMobilityIcon />,
    resource: "talent_mobility",
    actions: ["view"],
  },
  {
    key: "/learning-catalyst",
    label: "Learning",
    icon: <BookOutlined/>,
    resource: "learning_catalyst",
    actions: ["view"],
  },
  {
    key: "/profile",
    label: "Profile",
    icon: <UserOutlined/>,
    resource: "profile",
    actions: ["view"],
  },
  {
    key: "/roles",
    label: "Roles",
    icon: <SafetyCertificateOutlined/>,
    resource: "roles",
    actions: ["inprogress"],
  },
  {
    key: "/employees",
    label: "Employees",
    icon: <IdcardOutlined/>,
    resource: "employee",
    actions: ["view"],
  },
  // Employee views
  {
    key: "/Skills-bridger-employee",
    label: "Skills Bridger",
    icon: <SkillsBridgersIcon />,
    resource: "skillsbridger_employee",
    actions: ["view"],
  },
  {
    key: "/talent-mobility-employee",
    label: "Talent Mobility",
    icon: <TalentMobilityIcon />,
    resource: "talent_mobility_employee",
    actions: ["view"],
  },
  {
    key: "/learning-catalyst-employee",
    label: "Learning Catalyst",
    icon: <LearningCatalystIcon />,
    resource: "learning_catalyst_employee",
    actions: ["view"],
  },
];

// Resolve the first route a role can open on login/role-switch, in sidebar
// order (NAV_ITEMS — unlike routeConfigV2, it has exactly one param-free
// entry per module, so this can never resolve to an unresolved ":param"
// path). WIP resources (isWipOnly — see RouteConfigV2.js, e.g. Dashboard,
// Skills Bridger) are checked last and only used as a landing page if
// nothing else matches — otherwise the first WIP module a role happens to
// hold would hijack the landing page ahead of real modules like Jobs
// Library, purely because of its position in NAV_ITEMS' (sidebar-display)
// order.
// Falls through to Admin Setup (kept separate since its path is dynamic and
// it has no single resource), else /not-authorized.
// Admin Setup entry point is picked by entitlement, not role name: platform
// admins (system_admin_setup) land on /system-admin-setup, tenant admins on /admin-setup.
export const getAdminSetupPath = (allows) =>
  allows("system_admin_setup", ["view"]) ? "/system-admin-setup" : "/admin-setup";

export const firstSidebarRoute = (allows) => {
  const candidates = NAV_ITEMS.filter(({ resource, actions }) => allows(resource, actions));
  const item =
    candidates.find(({ actions }) => !isWipOnly(actions)) ?? candidates[0];
  if (item) return item.key;
  if (allows("admin_setup", ["view"]) || allows("system_admin_setup", ["view"])) return getAdminSetupPath(allows);
  return "/not-authorized";
};

// ─── Component ────────────────────────────────────────────────────────────────

const LayoutV2 = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Sidebar/header links route through here instead of plain <Link> navigation
  // so an open Dashboard widget gets closed (and its synthetic history entry
  // actually unwound) before we navigate away — see dashboardWidgetHistory.js.
  // Modified clicks (new tab, etc.) are left to the browser's native handling.
  // Reads the target from the link's own href rather than taking it as a
  // second argument, so it can't drift out of sync with the Link's `to`.
  const handleNavLinkClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) {
      return;
    }
    event.preventDefault();
    const to = event.currentTarget.getAttribute("href");
    closeDashboardWidgetForNavigation(() => navigate(to));
  };

  const {
    profileName,
    setProfileRole,
    orgDomain,
    logout,
    setEntitlements,
    email,
  } = useContext(AuthContext);

  // V2: orgMetaData / entitlements / selectedJob from Zustand (prevent layout
  // re-renders on unrelated auth state changes, and pick up store refreshes).
  const orgMetaData = useAuthStore((s) => s.orgMetaData);
  const { entitlementsData, profileRole } = useEntitlementsStore();
  const { hasAccess } = useHasAccess();
  const selectedJob = useJobsStore((state) => state.selectedJob);

  const adminSetupPath = getAdminSetupPath(hasAccess);
  const canSeeAdminSetup =
    hasAccess('admin_setup', ['view']) || hasAccess('system_admin_setup', ['view']);

  const notificationCount = useNotificationStoreV2((state) => state.notifications.length);
  // Set by the current module (via useTourStore.registerTour); null when the
  // page has no tour, in which case the help button is hidden.
  const startModuleTour = useTourStore((state) => state.starter);

  const {
    buildId:          abBuildId,
    buildStatus:      abStatus,
    isMinimized:      abMinimized,
    returnPath:       abReturnPath,
    setMinimized:     abSetMinimized,
    setPendingExpand: abSetPendingExpand,
    clearBuild:       abClearBuild,
  } = useAgentBuilderStore();

  // Show the global pill when the panel is minimized/dismissed but a build is tracked
  const showGhostPill = abMinimized && !!abBuildId;

  const ghostPillLabel = abStatus === "DONE"
    ? "Agent Built"
    : abStatus === "FAILED" || abStatus === "TIMED_OUT"
    ? "Build Failed"
    : "Building…";

  const ghostPillColor = abStatus === "DONE"   ? "#137333"
    : abStatus === "FAILED" || abStatus === "TIMED_OUT" ? "#c5221f"
    : "#1a73e8";

  const handleGhostPillExpand = () => {
    abSetMinimized(false);
    if (abReturnPath && location.pathname !== abReturnPath) {
      // Flag for PRD page: it should open the panel immediately on mount
      abSetPendingExpand(true);
      closeDashboardWidgetForNavigation(() => navigate(abReturnPath));
    }
    // If already on the PRD page, abSetMinimized(false) above triggers
    // the existing useEffect in SbPACJdPrdViewV2 that re-opens the panel.
  };

  // ── Local State ────────────────────────────────────────────────────────────

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [showFeedbackToast, setShowFeedbackToast] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [feedbackFiles, setFeedbackFiles] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [activeKey, setActiveKey] = useState(location.pathname);

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    const pathname = location.pathname;
    const basePath = pathname.split("/")[1];
    setActiveKey(`/${basePath}`);
  }, [location]);

  // Talent Mobility plan drafts live only while the user stays inside their
  // flow — navigating from one of a flow's screens to anywhere else (sidebar
  // nav, back to the Talent Mobility landing, ...) drops that draft so the
  // next visit starts from a clean plan.
  const previousLocationRef = useRef(location);
  useEffect(() => {
    const previousLocation = previousLocationRef.current;
    previousLocationRef.current = location;
    if (
      (isRedeploymentFlowLocation(previousLocation) && !isRedeploymentFlowLocation(location)) ||
      (isPaicReductionLocation(previousLocation) && !isPaicReductionLocation(location))
    ) {
      useRedeploymentPlanStore.getState().reset();
    }
    if (isWorkforceFlowLocation(previousLocation) && !isWorkforceFlowLocation(location)) {
      useWorkforcePlanStore.getState().reset();
    }
    if (isPaicRedesignFlowLocation(previousLocation) && !isPaicRedesignFlowLocation(location)) {
      usePaicRedesignPlanStore.getState().reset();
    }
  }, [location]);

  // Consent gate: redirect to /legal until current-version consent is recorded.
  const legalEnabled = useConsentGate(navigate);

  useEffect(() => {
    if (profileName) {
      const [fn, ln] = profileName.split(" ");
      const cap = (str) => {
        if (!str) return "";
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
      };
      setFirstName(cap(fn));
      setLastName(cap(ln));
    }
  }, [profileName]);


  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleBellOpen = () => setIsNotifOpen(true);
  const handleBellClose = () => setIsNotifOpen(false);

  const handleLogout = () => {
    useNotificationStoreV2.getState().clearAll();
    logout();
  };

  const handleSwitchRole = (selectedRole) => {
    const roleObj = entitlementsData.find((r) => r.role === selectedRole);
    if (!roleObj) return;
    setProfileRole(selectedRole);
    setEntitlements(roleObj.resources);
    useEntitlementsStore.getState().setProfileRole(selectedRole)
    useEntitlementsStore.getState().setEntitlements(roleObj.resources)

    // Always open the new role's first sidebar icon on switch. Check against the
    // new role's own resources — the store's hasAccess still reflects the old
    // role at this point.
    const allows = (resource, actions = []) => {
      if (!resource) return true;
      const granted = roleObj.resources?.find((r) => r.name === resource)?.entitlements ?? [];
      return granted.includes("*") || actions.some((a) => granted.includes(a));
    };
    closeDashboardWidgetForNavigation(() => navigate(firstSidebarRoute(allows)));
  };

  const handleFeedbackSubmit = async ({ feedback_type, comment: feedbackComment }) => {
    setFeedbackLoading(true);
    try {
      let response;
      if (feedbackFiles.length > 0) {
        const formData = new FormData();
        formData.append("feedback_type", feedback_type ?? "");
        formData.append("feedback_text", feedbackComment);
        formData.append("uri_at_time_of_feedback", location.pathname);
        formData.append(
          "sb_unique_id",
          location.pathname?.includes("/job-details")
            ? selectedJob?.sb_unique_id
            : ""
        );
        feedbackFiles.forEach((file) => {
          formData.append("files", file);
        });
        response = await submitUserFeedback(formData);
      } else {
        const payload = {
          feedback_type: feedback_type ?? null,
          feedback_text: feedbackComment,
          uri_at_time_of_feedback: location.pathname,
          sb_unique_id: location.pathname?.includes("/job-details")
            ? selectedJob?.sb_unique_id
            : null,
        };
        response = await submitUserFeedback(payload);
      }
      if (response?.message) {
        setShowSuccessAlert(true);
      }
    } finally {
      setFeedbackText("");
      setFeedbackFiles([]);
      setShowFeedbackToast(false);
      setFeedbackLoading(false);
    }
  };


  const getInitials = (fn, ln) => {
    return `${fn.charAt(0).toUpperCase()}${ln.charAt(0).toUpperCase()}`;
  };


  // ── Profile Dropdown Content ───────────────────────────────────────────────

  const profileDropdownContent = (
    <ProfileMenuStyled>
      <ProfileNameStyled>
        <ProfileFullNameStyled>
          {firstName} {lastName}
        </ProfileFullNameStyled>
        {profileRole === "online_user" ? (
          <ProfileRoleStyled>{orgDomain}</ProfileRoleStyled>
        ) : (
          <ProfileRoleStyled>{profileRole}</ProfileRoleStyled>
        )}
      </ProfileNameStyled>

      {/* Role Switcher */}
      {profileRole !== "online_user" && entitlementsData.length > 1 && (
        <>
          <SwitchRoleLabelStyled>Switch Profile </SwitchRoleLabelStyled>
          {entitlementsData.map((item) => (
            <ProfileMenuItemStyled
              key={item.role}
              active={profileRole === item.role}
              onClick={() => handleSwitchRole(item.role)}
            >
              {item.role}
            </ProfileMenuItemStyled>
          ))}
          <ProfileMenuDividerStyled />
        </>
      )}

      <ProfileMenuItemStyled danger
        onClick={handleLogout}
      >
        <LogoutOutlined />
        Logout
      </ProfileMenuItemStyled>
    </ProfileMenuStyled>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <LayoutWrapperStyled>
      <V2GlobalStyle />
      {/* ── Fixed Header ── */}
      <HeaderStyled>
        {/* Decorative brand bridge motif (behind logo + actions): full-width self-drawing line
            plus constant round nodes positioned on the line's valleys (web node positions). */}
        <HeaderBridgeStyled aria-hidden="true">
          <HeaderBridgeAnimation />
        </HeaderBridgeStyled>

        {/* Logo */}
        <LogoWrapperStyled>
          <Link to="/jobs-library" onClick={handleNavLinkClick}>
            {profileRole !== "online_user" && orgMetaData?.org_logo_url ? (
           <Image
            src={orgMetaData.org_logo_url}
            alt="Customer Logo"
            preview={false}
           />
            ) : (
              profileRole !== "online_user" &&
              !orgMetaData?.org_logo_url && (
                <CustomerLogoTextStyled>Customer Logo</CustomerLogoTextStyled>
              )
            )}
          </Link>
        </LogoWrapperStyled>

        {/* Header Right Actions */}
        <NavActionsStyled>
          {/* Take the Tour — shows only when the current module registered a tour */}
          {startModuleTour && (
            <SbTooltip content="Take the tour" placement="bottom">
              <TourHelpWrapperStyled onClick={startModuleTour}>
                <QuestionCircleOutlined />
              </TourHelpWrapperStyled>
            </SbTooltip>
          )}

          {/* Notification Bell */}
          <Dropdown
            open={isNotifOpen}
            onOpenChange={(open) => {
              if (open) handleBellOpen();
              else handleBellClose();
            }}
            popupRender={() => (
              <NotificationPanelV2
                onClose={handleBellClose}
              />
            )}
            trigger={["click"]}
            placement="bottomRight"
          >
            <BellWrapperStyled>
              <SbBadge count={notificationCount}>
                <BellFilled />
              </SbBadge>
            </BellWrapperStyled>
          </Dropdown>

          {/* Profile Avatar + Dropdown */}
          <Dropdown
            popupRender={() => profileDropdownContent}
            trigger={["click"]}
            placement="bottomRight"
          >
            <AvatarStyled title={`${firstName} ${lastName}`}>
              {firstName && lastName ? getInitials(firstName, lastName) : "?"}
            </AvatarStyled>
          </Dropdown>
        </NavActionsStyled>
      </HeaderStyled>

      {/* ── Body (Sidebar + Content) ── */}
      <BodyWrapperStyled>
        {/* ── Left Sidebar ── */}
        <SiderStyled>
          <SidebarScrollableStyled>
            {NAV_ITEMS.map((item) => {
              const navItem = (
                <NavItemStyled
                  as={Link}
                  to={item.key}
                  className={activeKey === item.key ? "active" : ""}
                  onClick={handleNavLinkClick}
                >
                  <NavIconWrapperStyled>{item.icon}</NavIconWrapperStyled>
                  {item.label}
                </NavItemStyled>
              );
              // Org Design has no `resource` (open to any authenticated
              // user — see RouteConfigV2.js) — render it unconditionally
              // rather than wrapping in Permission, which hides on a falsy
              // resource rather than allowing it through.
              return item.resource ? (
                <Permission key={item.key} resource={item.resource} actions={item.actions}>
                  {navItem}
                </Permission>
              ) : (
                <React.Fragment key={item.key}>{navItem}</React.Fragment>
              );
            })}

            {/* Admin Setup — system_admin_setup:view → /system-admin-setup, else admin_setup:view → /admin-setup */}
            {canSeeAdminSetup && (
              <NavItemStyled
                as={Link}
                to={adminSetupPath}
                className={
                  activeKey === "/admin-setup" || activeKey === "/system-admin-setup"
                    ? "active"
                    : ""
                }
                onClick={handleNavLinkClick}
              >
                <NavIconWrapperStyled>
                  <SettingOutlined />
                </NavIconWrapperStyled>
                Admin Setup
              </NavItemStyled>
            )}
          </SidebarScrollableStyled>

          {/* Sidebar Footer — Legal tab pinned above the SkillsBridge logo */}
          <SidebarFooterStyled>
            {/* Legal & Consent — shown only for orgs enabled by the BE */}
            {legalEnabled && (
              <LegalNavItemStyled
                as={Link}
                to="/legal"
                className={activeKey === "/legal" ? "active" : ""}
                onClick={handleNavLinkClick}
              >
                <NavIconWrapperStyled>
                  <LegalIcon />
                </NavIconWrapperStyled>
                Legal
              </LegalNavItemStyled>
            )}
            <SbIconStyled src={sbBtLogo} alt="SkillsBridge" />
          </SidebarFooterStyled>
        </SiderStyled>

        {/* ── Main Content ── */}
        <ContentStyled>
          <Outlet />

          {/* Submit Feedback — bottom-right corner. Hidden when Ask Allie is
              enabled, since Ask Allie's own drawer already has a Feedback tab. */}
          {!isAskAllieEnabled() && (
            <FeedbackButtonWrapperStyled>
              <SbPopover
                open={showFeedbackToast}
                onOpenChange={(open) => {
                  if (!open) {
                    setShowFeedbackToast(false);
                    setFeedbackText("");
                    setFeedbackFiles([]);
                  }
                }}
                placement="topRight"
                trigger="click"
                content={
                  <FeedbackV2
                    onClose={() => {
                      setShowFeedbackToast(false);
                      setFeedbackText("");
                      setFeedbackFiles([]);
                    }}
                    actionFunction={handleFeedbackSubmit}
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    disabled={feedbackLoading}
                    loading={feedbackLoading}
                    onFileUpload={setFeedbackFiles}
                  />
                }
              >
                <span>
                  <FeedbackButtonStyled
                    onClick={() => setShowFeedbackToast(true)}
                    title="Share Feedback"
                    disabled={feedbackLoading}
                  >
                    <ThumbsContainerStyled>
                      <FeedbackIcon />
                    </ThumbsContainerStyled>
                  </FeedbackButtonStyled>
                </span>
              </SbPopover>

              <SbSuccessAlert
                show={showSuccessAlert}
                onClose={() => setShowSuccessAlert(false)}
                title="Thank you for your feedback!"
                message="We're working on it — you will be notified when this is resolved."
              />
            </FeedbackButtonWrapperStyled>
          )}

          {/* Ask Allie — chat + feedback panel (Dev-only until fully rolled out; will move to the top nav bar once complete) */}
          {isAskAllieEnabled() && (
            <AskAllieButtonWrapperStyled>
              <AskAllie />
            </AskAllieButtonWrapperStyled>
          )}
        </ContentStyled>
      </BodyWrapperStyled>

      {/* ── Agent Builder global floating pill (persists across page navigation) ── */}
      {showGhostPill && (
        <div
          onClick={handleGhostPillExpand}
          title="Expand"
          style={{
            position: "fixed", bottom: 24, right: 24, zIndex: 1200,
            display: "flex", alignItems: "center", gap: 8,
            background: "#fff", borderRadius: 24, border: `2px solid ${ghostPillColor}`,
            padding: "8px 14px", boxShadow: "0 4px 16px rgba(0,0,0,0.18)", cursor: "pointer",
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: ghostPillColor, flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: "#202124", whiteSpace: "nowrap" }}>
            {ghostPillLabel}
          </span>
          <button
            style={{ border: "none", background: "none", cursor: "pointer", fontSize: 13, color: "#5f6368", padding: "0 2px" }}
            onClick={(e) => { e.stopPropagation(); abClearBuild(); }}
            title="Close"
          >
            ✕
          </button>
        </div>
      )}
    </LayoutWrapperStyled>
  );
};

export default LayoutV2;